/** Room kept above the first comment when the thread is scrolled to. */
export const COMMENTS_SCROLL_MARGIN = 20;

/**
 * Where native scrolls to, from the comments section's measured `y`.
 *
 * `null` means "not measured yet, do not consume the request". The section sits below the post
 * body, so a `y` of 0 is a layout that has not happened (or one measured against the wrong parent),
 * not a thread at the top: scrolling to it would do nothing and the request would be lost.
 */
export const nativeCommentsScrollY = (sectionY: number | null): number | null => {
  if (sectionY === null || sectionY <= 0) {
    return null;
  }

  return Math.max(0, sectionY - COMMENTS_SCROLL_MARGIN);
};

type ScrollIntoViewTarget = {
  scrollIntoView?: (options: { behavior: 'smooth' | 'auto'; block: 'start' }) => void;
};

/**
 * Web: ask the browser to bring the section into view. The browser reads the element's live
 * position and scrolls whichever ancestor actually scrolls, so it cannot go stale the way a `y`
 * recorded at layout time does (a position change above a section fires no layout event), and it
 * does not depend on the React Native scroll view being the scroller. The section carries
 * `scroll-mt-5` for the margin. Returns whether the request was delivered.
 */
export const scrollCommentsIntoView = (
  section: ScrollIntoViewTarget | null,
  reducedMotion: boolean
): boolean => {
  if (!section?.scrollIntoView) {
    return false;
  }

  section.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });

  return true;
};
